import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import MailIcon from '@mui/icons-material/Mail';
import Snackbar from '@mui/material/Snackbar';
import AlertTitle from '@mui/material/AlertTitle';
import { Box, styled } from '@mui/material';
import {
  GroupResponse,
  OrganisationResponse,
  PractitionerData,
  RequestError,
  useGetPractitionerByGroupAndEmail,
} from 'api';
import { InviteUserOrgOptions, LicensedProduct } from 'types/types';
import { useAuth } from 'contexts/AuthContext';
import { useInviteContext } from 'pages/AddUser/InviteProvider';
import { VALID_EMAIL_REGEX } from 'variables';
import UserSelect from 'components/UserSelect';
import useGetProducts from 'components/hooks/useGetProducts';
import {
  Alert,
  SectionTitle,
  Dialog,
  TextV2,
} from '@insights-ltd/design-library/components';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { isGroupManager } from 'utils/role';
import { useSubmitUser } from './useSubmitUser';
import { AddUserFormProps, FormData, ROLE_MAP } from './types';
import { OrgsAndDetailsSection } from './OrgsAndDetailsSection/OrgsAndDetailsSections';
import { useGetWarningModalContent } from './helper';

type ErrorSnackBarProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  text: string;
};

const StyledMailIcon = styled(MailIcon)(({ theme }) => ({
  fill: theme.palette.blue.main,
  height: '20px',
}));

const ErrorSnackBar = ({ open, onClose, text, title }: ErrorSnackBarProps) => (
  <Snackbar open={open} autoHideDuration={6000} onClose={onClose}>
    <Alert elevation={6} variant="filled" severity="error" onClose={onClose}>
      <AlertTitle>{title}</AlertTitle>
      {text}
    </Alert>
  </Snackbar>
);

const isGeneralError = (isError: boolean, error: any): boolean =>
  isError && !(error instanceof RequestError);

const AddUserForm = ({
  mutate,
  resetMutation,
  inviteUserError,
  isInviteUserError,
  currentOrgId,
  currentGroupId,
  initialPractitioner,
}: AddUserFormProps) => {
  const products = useGetProducts();
  const [isGmWarningOpen, setIsGmWarningOpen] = useState(false);

  const preselectedProducts = Object.entries(products)
    .filter(([, options]) => options.defaultSelected)
    .map(([product]) => product as LicensedProduct);

  const [preselectedPractitioner, setPreselectedPractitioner] =
    useState<PractitionerData | null>(initialPractitioner ?? null);
  const {
    currentGroup,
    groups,
    practitionerOrganisations,
    organisationsInGroups,
    formState,
    setFormState,
  } = useInviteContext();

  const isGroupedOrg = useMemo(
    () => Object.keys(organisationsInGroups).includes(currentOrgId),
    [organisationsInGroups, currentOrgId],
  );
  const { selectedGroup, selectedOrgs } = formState;

  const { t } = useTranslation();
  const { user } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors: formErrors },
    setValue,
    watch,
    control,
    getValues,
  } = useForm<FormData>({
    defaultValues: {
      organisationIds: [],
      licensedProducts: preselectedProducts,
      role: ROLE_MAP.STANDARD,
      emailAddress: undefined,
    },
  });

  register('role', { required: true });
  register('emailAddress', { required: true, pattern: VALID_EMAIL_REGEX });
  const formEmailAddress = watch('emailAddress');

  const setSelectedOrgs = (org: any) => {
    if (org?.id) {
      setFormState({ selectedOrgs: [...(formState.selectedOrgs ?? []), org] });
      setValue('licensedProducts', preselectedProducts);
    }

    if (org === null) {
      setFormState({ selectedOrgs: [] });
      setFormState({ permissionRole: 'STANDARD' });
      setValue('licensedProducts', []);
    }
  };
  const onRemoveOrg = (id: string) => {
    const filteredOrgsById =
      formState?.selectedOrgs?.filter(
        (org: OrganisationResponse) => org?.id !== id,
      ) ?? [];
    setFormState({ selectedOrgs: filteredOrgsById });

    if (filteredOrgsById.length === 0) {
      setFormState({ permissionRole: 'STANDARD' });
      setValue('licensedProducts', []);
    }
  };

  const {
    data: rawExistingPractitioner,
    isPending: emailPending,
    error: existingPractitionerError,
  } = useGetPractitionerByGroupAndEmail(
    currentGroupId,
    formEmailAddress,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    user!.permissions.Add_User_to_Partner_Organisation === 'Global' &&
      !preselectedPractitioner,
  );

  const existingPractitioner =
    preselectedPractitioner || rawExistingPractitioner;

  const userAlreadyHasAccess = useMemo(
    () =>
      !!practitionerOrganisations.find((item) => {
        if ((item as GroupResponse).organisations) {
          return !!(item as GroupResponse).organisations.find(
            ({ id: orgId }) => currentOrgId === orgId,
          );
        }
        return currentOrgId === item.id;
      }),
    [currentOrgId, practitionerOrganisations],
  );

  const { onSubmit } = useSubmitUser(
    selectedGroup,
    existingPractitioner,
    selectedOrgs,
    organisationsInGroups,
    handleSubmit,
    mutate,
  );

  const resetFormValues = () => {
    setFormState({
      permissionRole: 'STANDARD',
      orgType: InviteUserOrgOptions.ONE_ORG,
    });
    setSelectedOrgs(null);
  };

  const emailValidationError =
    !!existingPractitionerError &&
    existingPractitionerError instanceof RequestError &&
    existingPractitionerError.errorCodes.find(
      (code) => code === 'INVALID_EMAIL_ADDRESS',
    );

  useEffect(() => {
    if (existingPractitioner) {
      if (!currentGroup && groups && currentGroupId) {
        const newGroup = groups.find(({ id }) => id === currentGroupId);
        if (newGroup !== formState.currentGroup) {
          setFormState({
            ...formState,
            currentGroup: newGroup,
          });
        }
      }
      if (!formEmailAddress && preselectedPractitioner) {
        setValue('emailAddress', preselectedPractitioner.emailAddress);
      }
      setValue('licensedProducts', existingPractitioner.licensedProducts);
      setValue('role', existingPractitioner.role);
    } else {
      setValue('licensedProducts', [...getValues('licensedProducts')]);
      setValue('role', ROLE_MAP[formState?.permissionRole ?? 'STANDARD']);
    }
    if (formState.existingPractitioner !== existingPractitioner) {
      setFormState({ ...formState, existingPractitioner });
    }
  }, [
    existingPractitioner,
    setValue,
    getValues,
    formEmailAddress,
    currentGroup,
    groups,
    currentGroupId,
    formState,
    preselectedPractitioner,
    setFormState,
  ]);

  const { selectedOrgGroup, formattedOrgNames } = useGetWarningModalContent({
    selectedOrgs: formState.selectedOrgs,
    groups,
  });

  const showGmWarning =
    isGroupManager(existingPractitioner?.role) && !!selectedOrgGroup;

  return (
    <form
      noValidate
      data-testid="add-user-form-v2"
      onSubmit={(event) => {
        if (showGmWarning) {
          event.preventDefault();
          setIsGmWarningOpen(true);
          return;
        }
        onSubmit(event);
      }}
    >
      <Box
        sx={{
          pb: spacingSizeMap.M,
        }}
      >
        <SectionTitle
          icon={{ icon: <StyledMailIcon />, iconColour: 'blue' }}
          text={t('ui.event-management.users.invite.check-email')}
        />
      </Box>
      <UserSelect
        error={existingPractitionerError}
        formErrors={formErrors}
        emailValue={formEmailAddress}
        onEmailSelected={(emailAddress) => {
          setValue('emailAddress', emailAddress);
          resetFormValues();
        }}
        onClear={() => {
          setValue('emailAddress', undefined);
          setPreselectedPractitioner(null);
        }}
        userAlreadyHasAccess={userAlreadyHasAccess}
        isGroupedOrg={isGroupedOrg}
      />
      {!emailValidationError ? (
        <OrgsAndDetailsSection
          formEmailAddress={formEmailAddress}
          selectedOrgs={selectedOrgs}
          setSelectedOrgs={setSelectedOrgs}
          onRemoveOrg={onRemoveOrg}
          control={control}
          setValue={setValue}
          currentOrgId={currentOrgId}
          emailLoading={preselectedPractitioner ? false : emailPending}
          userAlreadyHasAccess={userAlreadyHasAccess}
        />
      ) : null}
      <ErrorSnackBar
        open={isGeneralError(isInviteUserError, inviteUserError)}
        onClose={resetMutation}
        title={t('ui.event-management.users.create.error.title')}
        text={t('ui.event-management.users.create.error.details')}
      />
      {showGmWarning && (
        <Dialog
          open={isGmWarningOpen}
          onClose={() => setIsGmWarningOpen(false)}
          title={t(
            'ui.event-management.users.invite.group-manager.warning.modal.title',
          )}
          content={
            <>
              <TextV2 color="textSecondary">
                <Trans
                  i18nKey="ui.event-management.users.invite.group-manager.warning.modal.sub-title"
                  t={t}
                  values={{ organisationName: formattedOrgNames }}
                  components={{
                    bold: (
                      <TextV2
                        component="span"
                        variant="bodyBold"
                        color="textSecondary"
                      />
                    ),
                  }}
                />
              </TextV2>
              <Alert severity="warning" sx={{ mt: spacingSizeMap.L }}>
                {t(
                  'ui.event-management.users.invite.group-manager.warning.modal.message',
                  { groupName: selectedOrgGroup?.name },
                )}
              </Alert>
            </>
          }
          primaryButtonText={t(
            'ui.event-management.users.invite.group-manager.warning.modal.submit-button-text',
          )}
          secondaryButtonText={t(
            'ui.event-management.users.invite.group-manager.warning.modal.cancel-button-text',
          )}
          onSubmit={() => {
            setIsGmWarningOpen(false);
            onSubmit();
          }}
        />
      )}
    </form>
  );
};

export default AddUserForm;
